import "./styles.css";
import { enGB } from 'date-fns/locale';

import React, { useState, useEffect, useMemo, useRef } from "react";
import { 
  Paper, 
  Typography, 
  Grid, 
  Button, 
  Chip, 
  Card, 
  CardContent, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  TextField,
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
  Snackbar,
  Alert,
  AlertTitle
} from '@mui/material';
import { 
  EventAvailable, 
  Sick, 
  Warning, 
  CheckCircle, 
  Cancel, 
  AccessTime, 
  DateRange, 
  ArrowUpward, 
  ArrowDownward, 
  FilterList, 
  Close, 
  Add,
  Inventory2,
  Refresh
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format, differenceInDays, isBefore, isAfter, parseISO } from 'date-fns';
import { getLeaveTypes, applyForLeave, getUserLeaveHistory } from '../../../Action/api';
import { useNavigate, useLocation } from 'react-router-dom';
import { getUserDataFromToken } from '../../../Utils/urlAuthHandler';

const Leave = () => {
  // Add navigate function
  const navigate = useNavigate();
  const location = useLocation();
  
  // Initialize leave balance with empty values - will be populated from API
  const [leaveBalance, setLeaveBalance] = useState({
    annual: { total: 0, used: 0, remaining: 0 },
    sick: { total: 0, used: 0, remaining: 0 },
    emergency: { total: 0, used: 0, remaining: 0 },
    unpaid: { total: 0, used: 0, remaining: 0 }
  });
  
  // NEW EFFECT - Add this here
  useEffect(() => {
    fetchLeaveHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const mockLeaveHistory = [
    { 
      id: 1, 
      type: 'Annual Leave', 
      startDate: '2025-05-15', 
      endDate: '2025-05-20', 
      days: 6, 
      status: 'Approved', 
      appliedOn: '2025-04-30',
      approvedBy: 'Manager Name',
      reason: 'Family vacation'
    },
    { 
      id: 2, 
      type: 'Sick Leave', 
      startDate: '2025-06-10', 
      endDate: '2025-06-11', 
      days: 2, 
      status: 'Approved', 
      appliedOn: '2025-06-10',
      approvedBy: 'Manager Name',
      reason: 'Fever'
    },
    { 
      id: 3, 
      type: 'Annual Leave', 
      startDate: '2025-07-05', 
      endDate: '2025-07-09', 
      days: 5, 
      status: 'Pending', 
      appliedOn: '2025-06-20',
      approvedBy: null,
      reason: 'Personal time off'
    },
    { 
      id: 4, 
      type: 'Emergency Leave', 
      startDate: '2025-03-18', 
      endDate: '2025-03-18', 
      days: 1, 
      status: 'Rejected', 
      appliedOn: '2025-03-17',
      approvedBy: 'Manager Name',
      reason: 'Family emergency'
    }
  ];
  
  // State hooks
  const [leaveHistory, setLeaveHistory] = useState(mockLeaveHistory);
  const [openLeaveDialog, setOpenLeaveDialog] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [leaveType, setLeaveType] = useState('');
  const [reason, setReason] = useState('');
  const [sortField, setSortField] = useState('appliedOn');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterStatus, setFilterStatus] = useState('All');
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [calculatedDays, setCalculatedDays] = useState(0);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [isLoadingLeaves, setIsLoadingLeaves] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [openError, setOpenError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [openSuccess, setOpenSuccess] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  
  // Add a new state variable to track if defaults are being used
  const [isUsingDefaults, setIsUsingDefaults] = useState(true);
  
  // Add these state variables near the other state declarations
  const [conflictingTasks, setConflictingTasks] = useState([]);
  const [taskConflictMessage, setTaskConflictMessage] = useState("");
  const [showTaskConflict, setShowTaskConflict] = useState(false);
  
  // Fetch leave data from API
  const fetchLeaveData = async (countryCode) => {
    if (!countryCode) return;
    
    setIsLoadingLeaves(true);
    
    try {
      const response = await getLeaveTypes(countryCode);
      
      if (response && Array.isArray(response)) {
        setLeaveTypes(response);
        
        // Update leave balance based on API data if needed
        const annualLeave = response.find(leave => leave.leaveName === 'Annual Leave');
        const emergencyLeave = response.find(leave => leave.leaveName === 'Emergency Leave');
        const sickLeave = response.find(leave => leave.leaveName === 'Sick Leave');
        
        if (annualLeave || emergencyLeave || sickLeave) {
          setLeaveBalance(prev => ({
            annual: {
              total: annualLeave ? parseInt(annualLeave.leaveCount) : prev.annual.total,
              used: prev.annual.used,
              remaining: annualLeave ? 
                parseInt(annualLeave.leaveCount) - prev.annual.used : 
                prev.annual.remaining
            },
            sick: {
              total: sickLeave ? parseInt(sickLeave.leaveCount) : prev.sick.total,
              used: prev.sick.used,
              remaining: sickLeave ? 
                parseInt(sickLeave.leaveCount) - prev.sick.used : 
                prev.sick.remaining
            },
            emergency: {
              total: emergencyLeave ? parseInt(emergencyLeave.leaveCount) : prev.emergency.total,
              used: prev.emergency.used,
              remaining: emergencyLeave ? 
                parseInt(emergencyLeave.leaveCount) - prev.emergency.used : 
                prev.emergency.remaining
            }
          }));
        }
      } else {
        setLeaveTypes([]);
      }
    } catch (error) {
      console.error('Error fetching leave data:', error);
      setLeaveTypes([]);
    } finally {
      setIsLoadingLeaves(false);
    }
  };
  
  // Fetch leave history from API
  const fetchLeaveHistory = async () => {
    setIsLoadingHistory(true);
    
    try {
      // Get user ID from token
      const { userId, emailId } = getUserDataFromToken();
      const userIdToUse = userId || emailId || '';
      
      if (!userIdToUse) {
        setErrorMessage("User ID not found. Please log out and log in again.");
        setOpenError(true);
        setIsLoadingHistory(false);
        return;
      }
      
      const response = await getUserLeaveHistory(userIdToUse);
      
      if (response && response.status) {
        const normalizeLeaveItem = (leave, index) => {
          const type = leave.leaveType || leave.type || leave.leaveName || leave.leaveCategory || '';
          const startDate = leave.leaveStartDate || leave.startDate || leave.fromDate || leave.start || '';
          const endDate = leave.leaveEndDate || leave.endDate || leave.toDate || leave.end || startDate;
          const days = Number(leave.numberOfDays ?? leave.days ?? leave.totalDays ?? 0);
          const status = leave.leaveStatus || leave.status || 'Pending';
          const appliedOn = leave.leaveAppliedOn || leave.appliedOn || leave.createdAt || startDate;
          const reason = leave.leaveReason || leave.reason || '';

          return {
            id: leave.id || index + 1,
            type,
            startDate,
            endDate,
            days,
            status,
            appliedOn,
            approvedBy: leave.approvedBy || leave.approvedManager || null,
            reason
          };
        };

        const mapTypeToKey = (type) => {
          const normalized = String(type).toLowerCase();
          if (normalized.includes('annual')) return 'annual';
          if (normalized.includes('sick')) return 'sick';
          if (normalized.includes('emergency')) return 'emergency';
          if (normalized.includes('unpaid')) return 'unpaid';
          if (normalized.includes('maternity')) return 'maternity';
          return null;
        };

        const rawItems =
          (response.leaveHistory && Array.isArray(response.leaveHistory.items)
            ? response.leaveHistory.items
            : Array.isArray(response.leaves)
              ? response.leaves
              : []);

        const formattedLeaves = rawItems.map(normalizeLeaveItem);
        setLeaveHistory(formattedLeaves);

        const usedByType = formattedLeaves.reduce(
          (acc, leave) => {
            if (leave.status === 'Rejected') return acc;
            const key = mapTypeToKey(leave.type);
            if (!key) return acc;
            acc[key] += Number(leave.days) || 0;
            return acc;
          },
          { annual: 0, sick: 0, emergency: 0, unpaid: 0, maternity: 0 }
        );

        if (response.allocation || response.leaveAllocation) {
          const allocation = response.allocation || {};
          const legacyAllocation = response.leaveAllocation || {};

          const annualTotal =
            allocation.allocatedAnnualLeave ?? legacyAllocation.allocated?.annual ?? 0;
          const sickTotal =
            allocation.allocatedSickLeave ?? legacyAllocation.allocated?.sick ?? 0;
          const emergencyTotal =
            allocation.allocatedEmergencyLeave ?? legacyAllocation.allocated?.emergency ?? 0;
          const unpaidTotal =
            allocation.allocatedUnpaidLeave ?? legacyAllocation.allocated?.unpaid ?? 0;

          setLeaveBalance({
            annual: {
              total: annualTotal,
              used: legacyAllocation.used?.annual ?? usedByType.annual,
              remaining:
                legacyAllocation.remaining?.annual ??
                Math.max(annualTotal - usedByType.annual, 0)
            },
            sick: {
              total: sickTotal,
              used: legacyAllocation.used?.sick ?? usedByType.sick,
              remaining:
                legacyAllocation.remaining?.sick ?? Math.max(sickTotal - usedByType.sick, 0)
            },
            emergency: {
              total: emergencyTotal,
              used: legacyAllocation.used?.emergency ?? usedByType.emergency,
              remaining:
                legacyAllocation.remaining?.emergency ??
                Math.max(emergencyTotal - usedByType.emergency, 0)
            },
            unpaid: {
              total: unpaidTotal,
              used: legacyAllocation.used?.unpaid ?? usedByType.unpaid,
              remaining:
                legacyAllocation.remaining?.unpaid ?? Math.max(unpaidTotal - usedByType.unpaid, 0)
            }
          });
        }
      } else {
        // Handle invalid response
        setLeaveHistory([]);
        setErrorMessage("Failed to load leave data. Invalid response from server.");
        setOpenError(true);
      }
    } catch (error) {
      console.error('Error fetching leave history:', error);
      setErrorMessage("Failed to load leave history. Please try again.");
      setOpenError(true);
      setLeaveHistory([]); // Reset to empty if error
    } finally {
      setIsLoadingHistory(false);
    }
  };
  
  // Fetch leave history when component mounts
  useEffect(() => {
    fetchLeaveHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Sort and filter leave history
  const sortedAndFilteredLeaves = useMemo(() => {
    let filtered = [...leaveHistory];
    
    if (filterStatus !== 'All') {
      filtered = filtered.filter(leave => leave.status === filterStatus);
    }
    
    return filtered.sort((a, b) => {
      let comparison = 0;
      if (sortField === 'startDate') {
        comparison = new Date(a.startDate) - new Date(b.startDate);
      } else if (sortField === 'days') {
        comparison = a.days - b.days;
      } else if (sortField === 'appliedOn') {
        comparison = new Date(a.appliedOn) - new Date(b.appliedOn);
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [leaveHistory, sortField, sortDirection, filterStatus]);
  
  // Handle sort change
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  // Open the leave application dialog
  const handleOpenLeaveDialog = () => {
    // Navigate to the apply leave page with necessary data as state
    navigate('/apply-leave', {
      state: {
        leaveTypes,
        leaveBalance,
        leaveHistory
      }
    });
  };
  
  // Close leave details dialog
  const handleCloseDetailsDialog = () => {
    setOpenDetailsDialog(false);
    setSelectedLeave(null);
  };
  
  // Handle leave application submission
  const handleSubmitLeave = async () => {
    if (!leaveType || !startDate || !endDate || !reason) {
      alert("Please fill all required fields");
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Get user ID from token
      const { userId } = getUserDataFromToken();
      
      if (!userId) {
        setErrorMessage("User ID not found. Please log out and log in again to refresh your session.");
        setOpenError(true);
        setIsLoading(false);
        return;
      }
      
      // Find the leave code based on type
      const selectedLeaveType = leaveTypes.find(leave => leave.leaveName === leaveType);
      const leaveCode = selectedLeaveType ? selectedLeaveType.leaveCode : '';
      
      // Prepare leave application data
      const leaveData = {
        userId: userId,
        leaveType: leaveType,
        leaveCode: leaveCode,
        leaveStartDate: format(startDate, 'yyyy-MM-dd'),
        leaveEndDate: format(endDate, 'yyyy-MM-dd'),
        leaveReason: reason,
        leaveAppliedOn: format(new Date(), 'yyyy-MM-dd'),
        leaveStatus: 'Pending'
      };
      
      const response = await applyForLeave(leaveData);
      console.log("API Response:", response);

      // if (response.isAxiosError) {
      //   console.log("API Error Response:", response.response);
      //    // Set state to show task conflicts in the dialog
      //   setConflictingTasks(response.conflictingTasks);
      //   setTaskConflictMessage(response.uiMsg || "There are tasks scheduled during your leave period.");
      //   setShowTaskConflict(true);
      // } 
      // Call the API
      console.log("API Response:", response);
      
      // Check if response contains task conflicts
      if (response && response.status === false && response.conflictingTasks) {
        // Set state to show task conflicts in the dialog
        setConflictingTasks(response.conflictingTasks);
        setTaskConflictMessage(response.uiMsg || "There are tasks scheduled during your leave period.");
        setShowTaskConflict(true);
        
        // Don't close the dialog since we want to show the conflict message
      } 
      
      else if (response && response.status) {
        // Success case
        // Create a new leave object for local state update
        const newLeave = {
          id: leaveHistory.length + 1,
          type: leaveType,
          startDate: format(startDate, 'yyyy-MM-dd'),
          endDate: format(endDate, 'yyyy-MM-dd'),
          days: calculatedDays,
          status: 'Pending',
          appliedOn: format(new Date(), 'yyyy-MM-dd'),
          approvedBy: null,
          reason: reason
        };
        
        // Update the leave history
        setLeaveHistory([newLeave, ...leaveHistory]);
        
        // Show success message
        setOpenError(false);
        setErrorMessage("Leave application submitted successfully!");
        setOpenSuccess(true);
        
        // Close the dialog
        // handleCloseLeaveDialog();
        
        // Refresh leave history and leave balance from API
        fetchLeaveHistory();
      } else {
        // Generic error case
        setErrorMessage("Failed to submit leave application. Please try again.");
        setOpenError(true);
      }
    } catch (error) {
      console.error('Error applying for leave:', error);
      setErrorMessage("Failed to submit leave application. Please try again.");
      setOpenError(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Modify handleLeaveTypeChange to update isUsingDefaults
  const handleLeaveTypeChange = (e) => {
    const selectedType = e.target.value;
    setLeaveType(selectedType);
    
    // Set default reason based on leave type
    if (selectedType === 'Annual Leave') {
      setReason("Planned family vacation that was scheduled in advance.");
    } else if (selectedType === 'Sick Leave') {
      setReason("Not feeling well due to a viral infection. Doctor has advised rest for a day.");
    } else if (selectedType === 'Emergency Leave') {
      setReason("An unexpected family situation has arisen and requires my urgent attention.");
    } else {
      setReason("");
    }
    
    // Set to false only if user selected something other than default
    if (selectedType !== 'Annual Leave') {
      setIsUsingDefaults(false);
    }
  };
  
  // Add handlers for detecting user changes to other fields
  const handleDateChange = (field, newValue) => {
    // First, determine if we're modifying default values
    const today = new Date();
    const isToday = newValue && 
      newValue.getDate() === today.getDate() && 
      newValue.getMonth() === today.getMonth() && 
      newValue.getFullYear() === today.getFullYear();
      
    if (!isToday) {
      setIsUsingDefaults(false);
    }

    if (field === 'start') {
      setStartDate(newValue);
      
      // If end date exists but is before the new start date, update end date to match start date
      if (endDate && isBefore(endDate, newValue)) {
        setEndDate(newValue);
      }
    } else {
      // Only allow end dates that are on or after the start date
      if (startDate && isBefore(newValue, startDate)) {
        // If user tries to select an end date before start date, set it equal to start date
        setEndDate(startDate);
      } else {
        setEndDate(newValue);
      }
    }
  };
  
  // Add handler for reason changes
  const handleReasonChange = (e) => {
    const newReason = e.target.value;
    setReason(newReason);
    
    // Check if the value matches any of our defaults
    const annualDefault = "Planned family vacation that was scheduled in advance.";
    const sickDefault = "Not feeling well due to a viral infection. Doctor has advised rest for a day.";
    const emergencyDefault = "An unexpected family situation has arisen and requires my urgent attention.";
    
    if (
      newReason !== annualDefault && 
      newReason !== sickDefault && 
      newReason !== emergencyDefault
    ) {
      setIsUsingDefaults(false);
    }
  };
  
  // Calculate days between start and end date
  useEffect(() => {
    if (startDate && endDate) {
      if (isBefore(endDate, startDate)) {
        setCalculatedDays(0);
      } else {
        // Add 1 to include both start and end days
        setCalculatedDays(differenceInDays(endDate, startDate) + 1);
      }
    } else {
      setCalculatedDays(0);
    }
  }, [startDate, endDate]);
  
  // Check if user has enough leave balance
  const hasEnoughBalance = () => {
    if (!leaveType || !startDate || !endDate) return true;
    
    const days = calculatedDays;
    
    if (leaveType === 'Annual Leave') {
      return days <= leaveBalance.annual.remaining;
    } else if (leaveType === 'Sick Leave') {
      return days <= leaveBalance.sick.remaining;
    } else if (leaveType === 'Emergency Leave') {
      return days <= leaveBalance.emergency.remaining;
    }
    
    return true;
  };
  
  // Add this function to check if selected dates overlap with existing approved leave applications
  const hasDateOverlap = () => {
    if (!startDate || !endDate) return false;
    
    // Only check for Approved leaves
    const conflictingLeaves = leaveHistory.filter(leave => 
      leave.status === 'Approved' &&
      ((isAfter(new Date(leave.startDate), startDate) && isBefore(new Date(leave.startDate), endDate)) ||
       (isAfter(new Date(leave.endDate), startDate) && isBefore(new Date(leave.endDate), endDate)) ||
       (isBefore(new Date(leave.startDate), startDate) && isAfter(new Date(leave.endDate), endDate)) ||
       (format(new Date(leave.startDate), 'yyyy-MM-dd') === format(startDate, 'yyyy-MM-dd')) ||
       (format(new Date(leave.endDate), 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd')))
    );
    
    return conflictingLeaves.length > 0;
  };
  
  // Function to get details of overlapping leaves for display
  const getOverlappingLeaveDetails = () => {
    if (!startDate || !endDate) return [];
    
    return leaveHistory.filter(leave => 
      leave.status === 'Approved' &&
      ((isAfter(new Date(leave.startDate), startDate) && isBefore(new Date(leave.startDate), endDate)) ||
       (isAfter(new Date(leave.endDate), startDate) && isBefore(new Date(leave.endDate), endDate)) ||
       (isBefore(new Date(leave.startDate), startDate) && isAfter(new Date(leave.endDate), endDate)) ||
       (format(new Date(leave.startDate), 'yyyy-MM-dd') === format(startDate, 'yyyy-MM-dd')) ||
       (format(new Date(leave.endDate), 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd')))
    );
  };
  
  // Get status chip color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved':
        return 'success';
      case 'Rejected':
        return 'error';
      case 'Pending':
        return 'warning';
      default:
        return 'default';
    }
  };
  
  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'Approved':
        return <CheckCircle fontSize="small" />;
      case 'Rejected':
        return <Cancel fontSize="small" />;
      case 'Pending':
        return <AccessTime fontSize="small" />;
      default:
        return null;
    }
  };
  
  // Handler for when the form submission completes
  const handleLeaveFormSubmit = (result) => {
    if (result.status) {
      // Success case
      if (result.newLeave) {
        // Update the leave history
        setLeaveHistory([result.newLeave, ...leaveHistory]);
      }
      
      // Show success message
      setOpenError(false);
      setErrorMessage(result.message);
      setOpenSuccess(true);
      
      // Refresh leave history and leave balance from API
      fetchLeaveHistory();
    } else {
      // Error case
      setErrorMessage(result.message);
      setOpenError(true);
    }
  };
  
  // Add these checks to your useEffect to handle returning from the form page
  useEffect(() => {
    if (location.state?.refreshData) {
      // Refresh data when navigating back from apply form
      fetchLeaveHistory();
      
      // Show success message if provided
      if (location.state.successMessage) {
        setErrorMessage(location.state.successMessage);
        setOpenSuccess(true);
      }
      
      // Clear location state
      navigate(location.pathname, { replace: true });
    }
  }, [location]);
  
  // First, add or uncomment this function if it's not already defined
  const handleOpenDetailsDialog = (leave) => {
    setSelectedLeave(leave);
    setOpenDetailsDialog(true);
  };
  
  return (
    <div className="leave-page">
      <Typography variant="h4" gutterBottom className="page-title">
        Leave Management
      </Typography>
      
      {/* Leave balance cards */}
      <Grid container spacing={3} className="leave-balance-section">
        <Grid item xs={12} md={4}>
          <Card className="leave-card annual-leave">
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography variant="h6" component="div" gutterBottom>
                  Annual Leave
                </Typography>
                <EventAvailable fontSize="large" color="primary" />
              </Box>
              
              <Box sx={{ mt: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Total
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {leaveBalance.annual.total} days
                  </Typography>
                </Box>
                
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Used
                  </Typography>
                  <Typography variant="body1">
                    {leaveBalance.annual.used} days
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 1.5 }} />
                
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Remaining
                  </Typography>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    {leaveBalance.annual.remaining} days
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card className="leave-card sick-leave">
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography variant="h6" component="div" gutterBottom>
                  Sick Leave
                </Typography>
                <Sick fontSize="large" color="error" />
              </Box>
              
              <Box sx={{ mt: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Total
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {leaveBalance.sick.total} days
                  </Typography>
                </Box>
                
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Used
                  </Typography>
                  <Typography variant="body1">
                    {leaveBalance.sick.used} days
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 1.5 }} />
                
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Remaining
                  </Typography>
                  <Typography variant="h6" color="error" fontWeight="bold">
                    {leaveBalance.sick.remaining} days
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card className="leave-card emergency-leave">
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography variant="h6" component="div" gutterBottom>
                  Emergency Leave
                </Typography>
                <Warning fontSize="large" color="warning" />
              </Box>
              
              <Box sx={{ mt: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Total
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {leaveBalance.emergency.total} days
                  </Typography>
                </Box>
                
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Used
                  </Typography>
                  <Typography variant="body1">
                    {leaveBalance.emergency.used} days
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 1.5 }} />
                
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Remaining
                  </Typography>
                  <Typography variant="h6" color="warning.dark" fontWeight="bold">
                    {leaveBalance.emergency.remaining} days
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Action button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, mb: 4 }}>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<Add />}
          onClick={handleOpenLeaveDialog}
          className="apply-leave-button"
        >
          Apply for Leave
        </Button>
      </Box>
      
      {/* Leave history section */}
      <Paper elevation={2} className="leave-history-section">
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="div">
            Leave History
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title="Refresh">
              <IconButton 
                aria-label="refresh" 
                onClick={fetchLeaveHistory} 
                disabled={isLoadingHistory}
                sx={{ mr: 1 }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
            
            <FormControl variant="outlined" size="small" sx={{ minWidth: 120, mr: 2 }}>
              <InputLabel id="filter-status-label">Status</InputLabel>
              <Select
                labelId="filter-status-label"
                id="filter-status"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Approved">Approved</MenuItem>
                <MenuItem value="Rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
            
            <Tooltip title="Filter List">
              <IconButton aria-label="filter list">
                <FilterList />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="leave history table">
            <TableHead>
              <TableRow>
                <TableCell className="table-header-cell">
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('type')}>
                    Type
                    {sortField === 'type' && (
                      sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell className="table-header-cell">
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('startDate')}>
                    Date
                    {sortField === 'startDate' && (
                      sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell className="table-header-cell">
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('days')}>
                    Days
                    {sortField === 'days' && (
                      sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell className="table-header-cell">Reason</TableCell>
                <TableCell className="table-header-cell">
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('appliedOn')}>
                    Applied On
                    {sortField === 'appliedOn' && (
                      sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell className="table-header-cell">Status</TableCell>
                <TableCell className="table-header-cell">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingHistory ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <CircularProgress size={40} sx={{ mb: 2 }} />
                      <Typography variant="body1" color="text.secondary">
                        Loading leave history...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : sortedAndFilteredLeaves.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
                      <Inventory2 sx={{ fontSize: 60, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="h6" color="text.secondary">No leave records found</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {filterStatus !== 'All' 
                          ? `No ${filterStatus.toLowerCase()} leave requests available` 
                          : 'Apply for a leave to see records here'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                sortedAndFilteredLeaves.map((leave) => (
                  <TableRow key={leave.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {leave.type === 'Annual Leave' && <EventAvailable color="primary" sx={{ mr: 1 }} />}
                        {leave.type === 'Sick Leave' && <Sick color="error" sx={{ mr: 1 }} />}
                        {leave.type === 'Emergency Leave' && <Warning color="warning" sx={{ mr: 1 }} />}
                        {leave.type}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <DateRange fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        {format(new Date(leave.startDate), 'dd MMM yyyy')}
                        {leave.endDate !== leave.startDate && (
                          <> - {format(new Date(leave.endDate), 'dd MMM yyyy')}</>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{leave.days} {leave.days === 1 ? 'day' : 'days'}</TableCell>
                    <TableCell>
                      <Tooltip title={leave.reason} arrow>
                        <Typography sx={{ 
                          maxWidth: 200, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {leave.reason}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{format(new Date(leave.appliedOn), 'dd MMM yyyy')}</TableCell>
                    <TableCell>
                      <Chip 
                        icon={getStatusIcon(leave.status)}
                        label={leave.status}
                        size="small"
                        color={getStatusColor(leave.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        onClick={() => handleOpenDetailsDialog(leave)}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Leave Details Dialog */}
      {selectedLeave && (
        <Dialog open={openDetailsDialog} onClose={handleCloseDetailsDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              Leave Details
              <IconButton edge="end" color="inherit" onClick={handleCloseDetailsDialog} aria-label="close">
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          
          <DialogContent dividers>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
              <Chip 
                icon={getStatusIcon(selectedLeave.status)}
                label={selectedLeave.status}
                color={getStatusColor(selectedLeave.status)}
                sx={{ px: 2, py: 3, fontSize: '1rem' }}
              />
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Leave Type</Typography>
                <Typography variant="body1" gutterBottom>{selectedLeave.type}</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Duration</Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedLeave.days} {selectedLeave.days === 1 ? 'day' : 'days'}
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Date Range</Typography>
                <Typography variant="body1" gutterBottom>
                  {format(new Date(selectedLeave.startDate), 'dd MMMM yyyy')}
                  {selectedLeave.startDate !== selectedLeave.endDate && (
                    <> to {format(new Date(selectedLeave.endDate), 'dd MMMM yyyy')}</>
                  )}
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Reason for Leave</Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default', mt: 0.5 }}>
                  <Typography variant="body1">{selectedLeave.reason}</Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Applied On</Typography>
                <Typography variant="body1" gutterBottom>
                  {format(new Date(selectedLeave.appliedOn), 'dd MMMM yyyy')}
                </Typography>
              </Grid>
              
              {selectedLeave.status === 'Approved' && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Approved By</Typography>
                  <Typography variant="body1" gutterBottom>{selectedLeave.approvedBy}</Typography>
                </Grid>
              )}
              
              {selectedLeave.status === 'Rejected' && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Rejected By</Typography>
                  <Typography variant="body1" gutterBottom>{selectedLeave.approvedBy}</Typography>
                </Grid>
              )}
            </Grid>
            
            {selectedLeave.status === 'Pending' && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                <Typography variant="body2" color="info.main">
                  Your leave request is pending approval from your manager. You'll be notified once it's reviewed.
                </Typography>
              </Box>
            )}
          </DialogContent>
          
          <DialogActions sx={{ px: 3, py: 2 }}>
            {selectedLeave.status === 'Pending' && (
              <Button variant="outlined" color="error" onClick={handleCloseDetailsDialog}>
                Cancel Request
              </Button>
            )}
            <Button onClick={handleCloseDetailsDialog}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
      
      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity} 
          sx={{ width: '100%' }}
        >
          <AlertTitle>{snackbarSeverity === 'error' ? 'Error' : 'Success'}</AlertTitle>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      
      {/* Error Snackbar */}
      <Snackbar 
        open={openError} 
        autoHideDuration={6000} 
        onClose={() => setOpenError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setOpenError(false)} 
          severity="error" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          <AlertTitle>Authentication Error</AlertTitle>
          {errorMessage}
        </Alert>
      </Snackbar>
      
      {/* Success Snackbar */}
      <Snackbar 
        open={openSuccess} 
        autoHideDuration={4000} 
        onClose={() => setOpenSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setOpenSuccess(false)} 
          severity="success" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          <AlertTitle>Success</AlertTitle>
          {errorMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Leave;